import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import {Box, Chip, Typography} from '@mui/material';

import {ProfileTabButton} from './ProfileTabButton';
import {
  careerCoachMilestoneChipSx,
  careerCoachPaperSx,
  careerCoachResourceLinkSx,
  careerCoachSectionLabelSx,
  careerCoachTrendIconSx,
  getCareerCoachGapTypeColors,
} from './workforceProfileCareerCoachSectionSx';
import {WorkforceProfileVersionEvolutionPanel} from './WorkforceProfileVersionEvolutionPanel';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import type {RequiredLanguageProfile} from '../../../../../types/shared-types';
import {getLocalisedPropertyValue} from '../../../../../utils/getLocalisedPropertyValue';
import {
  CareerCoachAnalysis,
  CareerCoachResource,
  hasLocalizedText,
  WorkforceProfileVersionEvolution,
} from '../../types/WorkforceProfileTypes';

const resourceTypeIcon = (type: CareerCoachResource['type']) => {
  const sx = {fontSize: 16, flexShrink: 0, mt: 0.25};
  switch (type) {
    case 'book':
      return <AutoStoriesOutlinedIcon sx={sx} />;
    case 'course':
      return <SchoolOutlinedIcon sx={sx} />;
    case 'certification':
      return <VerifiedOutlinedIcon sx={sx} />;
    case 'practice':
      return <FitnessCenterOutlinedIcon sx={sx} />;
    default:
      return null;
  }
};

export interface Props {
  analysis: CareerCoachAnalysis;
  versionEvolution?: WorkforceProfileVersionEvolution | null;
}

type CoachTab = 'development' | 'futureProofing' | 'evolution';

const resolveDefaultCoachTab = (
  hasDevelopment: boolean,
  hasFutureProofing: boolean,
  hasEvolution: boolean
): CoachTab => {
  if (hasDevelopment) {
    return 'development';
  }
  if (hasFutureProofing) {
    return 'futureProofing';
  }
  if (hasEvolution) {
    return 'evolution';
  }
  return 'development';
};

export const WorkforceProfileCareerCoachSection = ({analysis, versionEvolution}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceProfilesPage.detail.careerCoach',
  });
  const hasCoach = analysis.available;
  const {developmentPlan, futureProofing} = hasCoach ? analysis : {developmentPlan: null, futureProofing: null};
  const hasDevelopment = !!developmentPlan;
  const hasFutureProofing = !!futureProofing;
  const hasEvolution = !!versionEvolution?.available;
  const showTabs = [hasDevelopment, hasFutureProofing, hasEvolution].filter(Boolean).length > 1;

  const [activeTab, setActiveTab] = useState<CoachTab>(() =>
    resolveDefaultCoachTab(hasDevelopment, hasFutureProofing, hasEvolution)
  );

  useEffect(() => {
    if (activeTab === 'development' && !hasDevelopment) {
      setActiveTab(resolveDefaultCoachTab(false, hasFutureProofing, hasEvolution));
    }
    if (activeTab === 'futureProofing' && !hasFutureProofing) {
      setActiveTab(resolveDefaultCoachTab(hasDevelopment, false, hasEvolution));
    }
    if (activeTab === 'evolution' && !hasEvolution) {
      setActiveTab(resolveDefaultCoachTab(hasDevelopment, hasFutureProofing, false));
    }
  }, [activeTab, hasDevelopment, hasFutureProofing, hasEvolution]);

  if (!hasCoach && !hasEvolution) {
    const reason = analysis.reason?.trim();
    const showTechnicalReason = !!reason && !/timeout|TCPSocket|error/i.test(reason);

    return (
      <Box>
        <Box sx={{px: 0.5, pt: 0.5, pb: 1}}>
          <Typography variant='subtitle2' sx={{fontWeight: 600, mb: 0.5}}>
            {t('unavailableTitle')}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{lineHeight: 1.6}}>
            {showTechnicalReason ? reason : t('unavailableDescription')}
          </Typography>
        </Box>
        <EmptyStateCard message={t('unavailableEmptyMessage') as string} sx={{py: 2}} />
      </Box>
    );
  }

  const lang = currentLanguage === 'de' ? 'de' : 'en';
  const localized = (text: RequiredLanguageProfile) => getLocalisedPropertyValue(text, lang);

  return (
    <Box>
      {showTabs && (
        <Box
          sx={{
            display: 'flex',
            gap: 0,
            borderBottom: `1px solid ${orangeColors[3]}`,
            mb: 2,
            mx: -1,
            flexWrap: 'wrap',
          }}
        >
          {hasDevelopment && (
            <ProfileTabButton
              label={t('tabs.development')}
              active={activeTab === 'development'}
              onClick={() => setActiveTab('development')}
            />
          )}
          {hasFutureProofing && (
            <ProfileTabButton
              label={t('tabs.futureProofing')}
              active={activeTab === 'futureProofing'}
              onClick={() => setActiveTab('futureProofing')}
            />
          )}
          {hasEvolution && (
            <ProfileTabButton
              label={t('tabs.evolution')}
              active={activeTab === 'evolution'}
              onClick={() => setActiveTab('evolution')}
            />
          )}
        </Box>
      )}

      <Box>
        {activeTab === 'evolution' && hasEvolution && versionEvolution && (
          <WorkforceProfileVersionEvolutionPanel evolution={versionEvolution} />
        )}
        {activeTab === 'development' && developmentPlan && (
          <Box>
            {hasLocalizedText(developmentPlan.summary) && (
              <EmpionPaper sx={careerCoachPaperSx}>
                <Typography variant='h3' sx={{fontWeight: 600, mb: 1}}>
                  {t('developmentSummary')}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{
                    lineHeight: 1.6,
                  }}
                >
                  {localized(developmentPlan.summary)}
                </Typography>
              </EmpionPaper>
            )}

            {developmentPlan.priorityGaps.length > 0 && (
              <EmpionPaper sx={careerCoachPaperSx}>
                <Typography variant='h3' sx={{fontWeight: 600, mb: 2}}>
                  {t('priorityGaps')}
                </Typography>
                {developmentPlan.priorityGaps.map((gap, index) => (
                  <Box
                    key={`${localized(gap.skill)}-${index}`}
                    sx={{
                      mb: 2,
                      pb: 2,
                      borderBottom: `1px solid ${orangeColors[3]}`,
                      '&:last-child': {mb: 0, pb: 0, borderBottom: 0},
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant='body2' sx={{fontWeight: 600}}>
                        {localized(gap.skill)}
                      </Typography>
                      <Chip
                        label={t(`gapType.${gap.type}`)}
                        size='small'
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 600,
                          ...getCareerCoachGapTypeColors(gap.type),
                        }}
                      />
                    </Box>

                    <Box sx={{mb: 1}}>
                      <Typography variant='caption' sx={{...careerCoachSectionLabelSx, display: 'block', mb: 0.25}}>
                        {t('currentEvidence')}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{
                          lineHeight: 1.5,
                        }}
                      >
                        {localized(gap.currentEvidence)}
                      </Typography>
                    </Box>

                    <Box sx={{mb: 1}}>
                      <Typography variant='caption' sx={{...careerCoachSectionLabelSx, display: 'block', mb: 0.25}}>
                        {t('targetExpectation')}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{
                          lineHeight: 1.5,
                        }}
                      >
                        {localized(gap.targetExpectation)}
                      </Typography>
                    </Box>

                    {gap.recommendedActions.length > 0 && (
                      <Box>
                        <Typography variant='caption' sx={{...careerCoachSectionLabelSx, display: 'block', mb: 0.5}}>
                          {t('recommendedActions')}
                        </Typography>
                        {gap.recommendedActions.map((action, ai) => (
                          <Box
                            key={`${localized(action.action)}-${ai}`}
                            sx={{
                              display: 'flex',
                              gap: 1,
                              py: 0.75,
                              borderBottom: `0.5px solid ${orangeColors[3]}`,
                              '&:last-child': {borderBottom: 0},
                            }}
                          >
                            <Box sx={{flex: 1, minWidth: 0}}>
                              <Typography
                                variant='body2'
                                sx={{
                                  lineHeight: 1.5,
                                }}
                              >
                                {localized(action.action)}
                              </Typography>
                              {action.resource && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0.5,
                                    mt: 0.5,
                                  }}
                                >
                                  {resourceTypeIcon(action.resource.type)}
                                  <Typography variant='caption' sx={careerCoachResourceLinkSx}>
                                    {action.resource.title}
                                    {action.resource.authorOrProvider ? ` — ${action.resource.authorOrProvider}` : ''}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            {action.timeline && hasLocalizedText(action.timeline) && (
                              <Chip
                                label={localized(action.timeline)}
                                size='small'
                                sx={{
                                  height: 22,
                                  fontSize: 11,
                                  fontWeight: 500,
                                  bgcolor: orangeColors[5],
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </EmpionPaper>
            )}

            {developmentPlan.milestones.length > 0 && (
              <EmpionPaper sx={{...careerCoachPaperSx, mb: 0}}>
                <Typography variant='h3' sx={{fontWeight: 600, mb: 1.5}}>
                  {t('milestones')}
                </Typography>
                {developmentPlan.milestones.map((milestone) => (
                  <Box
                    key={milestone.month}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      py: 0.75,
                      borderBottom: `0.5px solid ${orangeColors[3]}`,
                      '&:last-child': {borderBottom: 0},
                    }}
                  >
                    <Chip
                      label={t('monthLabel', {month: milestone.month})}
                      size='small'
                      sx={careerCoachMilestoneChipSx}
                    />
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{
                        lineHeight: 1.5,
                      }}
                    >
                      {localized(milestone.goal)}
                    </Typography>
                  </Box>
                ))}
              </EmpionPaper>
            )}
          </Box>
        )}

        {activeTab === 'futureProofing' && futureProofing && (
          <Box>
            {hasLocalizedText(futureProofing.summary) && (
              <EmpionPaper sx={careerCoachPaperSx}>
                <Typography variant='h3' sx={{fontWeight: 600, mb: 1}}>
                  {t('futureProofingSummary')}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{
                    lineHeight: 1.6,
                  }}
                >
                  {localized(futureProofing.summary)}
                </Typography>
              </EmpionPaper>
            )}

            {futureProofing.marketTrends.length > 0 && (
              <EmpionPaper sx={careerCoachPaperSx}>
                <Typography variant='h3' sx={{fontWeight: 600, mb: 2}}>
                  {t('marketTrends')}
                </Typography>
                {futureProofing.marketTrends.map((trend, index) => (
                  <Box
                    key={`trend-${index}`}
                    sx={{
                      mb: 2,
                      pb: 2,
                      borderBottom: `1px solid ${orangeColors[3]}`,
                      '&:last-child': {mb: 0, pb: 0, borderBottom: 0},
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <TrendingUpIcon sx={careerCoachTrendIconSx} />
                      <Typography variant='body2' sx={{fontWeight: 600}}>
                        {localized(trend.trend)}
                      </Typography>
                    </Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{
                        lineHeight: 1.5,
                        mb: 0.5,
                        ml: 3.5,
                      }}
                    >
                      {localized(trend.relevance)}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        lineHeight: 1.5,
                        ml: 3.5,
                        fontStyle: 'italic',
                      }}
                    >
                      {localized(trend.recommendation)}
                    </Typography>
                  </Box>
                ))}
              </EmpionPaper>
            )}

            {futureProofing.emergingSkills.length > 0 && (
              <EmpionPaper sx={{...careerCoachPaperSx, mb: 0}}>
                <Typography variant='h3' sx={{fontWeight: 600, mb: 2}}>
                  {t('emergingSkills')}
                </Typography>
                {futureProofing.emergingSkills.map((skill, index) => (
                  <Box
                    key={`skill-${index}`}
                    sx={{
                      mb: 1.5,
                      pb: 1.5,
                      borderBottom: `1px solid ${orangeColors[3]}`,
                      '&:last-child': {mb: 0, pb: 0, borderBottom: 0},
                    }}
                  >
                    <Typography variant='body2' sx={{fontWeight: 600, mb: 0.5}}>
                      {localized(skill.skill)}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{
                        lineHeight: 1.5,
                      }}
                    >
                      {localized(skill.rationale)}
                    </Typography>
                    {skill.resource && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {resourceTypeIcon(skill.resource.type)}
                        <Typography variant='caption' sx={careerCoachResourceLinkSx}>
                          {skill.resource.title}
                          {skill.resource.authorOrProvider ? ` — ${skill.resource.authorOrProvider}` : ''}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </EmpionPaper>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
